import React, { useState, useEffect } from 'react';
import { StateSelect, normalizeStateToCode } from '@tradesmatepro/shared-ui';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../utils/supabaseClient';
import {
  MapPinIcon,
  PlusIcon,
  BuildingOfficeIcon,
  HomeIcon,
  BuildingStorefrontIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { formatAddressSafe } from '../utils/formatAddress';
import { showToast } from './Common/Toast';

// Statuses in which a work order is still a quote. "Address not set yet" is allowed only here
// (PRODUCT_DECISIONS: "Service address works like a property"); a job needs an address before
// it can be scheduled.
export const QUOTE_ADDRESS_OPTIONAL_STATUSES = ['draft', 'sent', 'presented', 'changes_requested', 'follow_up'];

// The customer's own address, read from both column families (a DB trigger keeps them equal,
// but either can be the populated one on an older row).
export const customerBillingAddress = (customer) => {
  if (!customer) return null;
  const line1 = customer.billing_address_line_1 || customer.street_address;
  const city = customer.billing_city || customer.city;
  const state = customer.billing_state || customer.state;
  const zip = customer.billing_zip_code || customer.zip_code;
  if (!line1 || !city || !state || !zip) return null;
  return {
    type: 'billing',
    label: "Customer's address",
    customer_id: customer.id,
    address_line_1: line1,
    address_line_2: customer.billing_address_line_2 || null,
    city,
    state,
    zip_code: zip,
    country: customer.billing_country || customer.country || 'United States'
  };
};

// True when the selection carries a real street address (not "Address not set yet").
export const isServiceAddressSet = (address) =>
  !!address && address.type !== 'none' && address.type !== 'tbd' && !!address.address_line_1;

// The database refuses to move a job into scheduled or in progress without a service address
// (trigger trg_scheduled_requires_service_address, migration
// 20261020_scheduled_requires_service_address). Returns that message for a toast when the
// error is this rule (raw, or wrapped by an RPC's own message), otherwise null so the caller
// keeps its usual text.
export const SERVICE_ADDRESS_REQUIRED_MESSAGE = 'Add the service address before scheduling this job.';
export const serviceAddressRequiredMessage = (error) =>
  String(error?.message || error || '').includes(SERVICE_ADDRESS_REQUIRED_MESSAGE)
    ? SERVICE_ADDRESS_REQUIRED_MESSAGE
    : null;

const NOT_SET = { type: 'none', label: 'Address not set yet' };
const EMPTY_NEW_ADDRESS = {
  address_name: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  zip_code: '',
  access_notes: ''
};

const inputClass = 'w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * One service-address picker for every customer type (PRODUCT_DECISIONS: "Service address
 * works like a property"): the customer's own address, any saved addresses (this company's
 * customer_addresses rows of type service), or Add address inline, which saves a
 * customer_addresses row and selects it. "Address not set yet" is offered only when
 * allowNotSet (the record is a quote).
 */
const ServiceAddressSelector = ({ customer, selectedAddress, onAddressChange, allowNotSet = false }) => {
  const { user } = useUser();
  const [serviceAddresses, setServiceAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState(EMPTY_NEW_ADDRESS);

  const customerType = (customer?.customer_type || customer?.type || '').toLowerCase();
  const isResidential = customerType !== 'commercial' && customerType !== 'industrial';
  const billing = customerBillingAddress(customer);
  // A selection made for a different customer (the customer was changed on the form) is not
  // this customer's address: treat it as no selection so the default is picked again.
  const effective = selectedAddress?.customer_id && customer?.id && selectedAddress.customer_id !== customer.id
    ? null
    : selectedAddress;

  // Load saved service addresses for the customer, then pick a default if nothing is chosen.
  useEffect(() => {
    if (customer?.id && user?.company_id) {
      loadServiceAddresses({ pickDefault: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, user?.company_id]);

  const loadServiceAddresses = async ({ pickDefault = false } = {}) => {
    setLoading(true);
    let addresses = [];
    try {
      const { data, error } = await supabase
        .from('customer_addresses')
        .select('*')
        .eq('customer_id', customer.id)
        .eq('company_id', user.company_id)
        .eq('address_type', 'service')
        .order('is_primary', { ascending: false, nullsFirst: false })
        .order('address_name', { ascending: true });
      if (error) {
        console.error('Error loading service addresses:', error);
      } else {
        addresses = data || [];
      }
    } catch (error) {
      console.error('Error loading service addresses:', error);
    } finally {
      setServiceAddresses(addresses);
      setLoading(false);
    }

    // Default when nothing is chosen yet: a residential job defaults to the customer's own
    // address, a commercial one to its primary saved location; either falls back to the other.
    if (pickDefault && !effective) {
      const primary = addresses.find(a => a.is_primary) || addresses[0] || null;
      const pick = isResidential ? (billing || primary) : (primary || billing);
      if (pick) onAddressChange(pick);
      // No address for this customer. On a quote that is "not set yet"; elsewhere it is only
      // sent when it replaces another customer's address, so that address is cleared on save
      // instead of staying on the record.
      else if (allowNotSet || selectedAddress) onAddressChange({ ...NOT_SET, customer_id: customer.id });
    }
  };

  const handleAddAddress = async () => {
    if (!newAddress.address_line_1.trim() || !newAddress.city.trim() || !newAddress.state || !newAddress.zip_code.trim()) {
      showToast('warning', 'Please fill in street, city, state and ZIP');
      return;
    }

    setSavingAddress(true);
    try {
      // The DB allows one primary per customer and company across ALL address types, so the
      // first saved service address is primary only if no other row already is.
      const { data: existingPrimary } = await supabase
        .from('customer_addresses')
        .select('id')
        .eq('customer_id', customer.id)
        .eq('company_id', user.company_id)
        .eq('is_primary', true)
        .limit(1);
      const makePrimary = serviceAddresses.length === 0 && !(existingPrimary && existingPrimary.length);

      const { data: createdAddress, error } = await supabase
        .from('customer_addresses')
        .insert({
          customer_id: customer.id,
          company_id: user.company_id,
          address_type: 'service',
          address_name: newAddress.address_name.trim() || null,
          address_line_1: newAddress.address_line_1.trim(),
          address_line_2: newAddress.address_line_2.trim() || null,
          city: newAddress.city.trim(),
          state: normalizeStateToCode(newAddress.state), // canonical 2-letter code, as AddressManager stores
          zip_code: newAddress.zip_code.trim(),
          access_notes: newAddress.access_notes.trim() || null,
          is_primary: makePrimary
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding service address:', error);
        showToast('error', `Failed to add service address: ${error.message}`);
        return;
      }

      await loadServiceAddresses();
      onAddressChange(createdAddress);
      setShowAddForm(false);
      setNewAddress(EMPTY_NEW_ADDRESS);
      showToast('success', 'Service address added');
    } catch (error) {
      console.error('Error adding service address:', error);
      showToast('error', `Failed to add service address: ${error.message}`);
    } finally {
      setSavingAddress(false);
    }
  };

  const getCustomerTypeIcon = () => {
    switch (customerType) {
      case 'commercial':
        return <BuildingOfficeIcon className="w-4 h-4" />;
      case 'industrial':
        return <BuildingStorefrontIcon className="w-4 h-4" />;
      default:
        return <HomeIcon className="w-4 h-4" />;
    }
  };

  const getCustomerTypeLabel = () => {
    switch (customerType) {
      case 'commercial':
        return 'Commercial';
      case 'industrial':
        return 'Industrial';
      default:
        return 'Residential';
    }
  };

  const formatAddress = (address) => formatAddressSafe(address);

  // Guard: customer may not be loaded yet (e.g. after inline creation)
  if (!customer) {
    return (
      <div className="text-sm text-slate-400 py-2">Loading customer info...</div>
    );
  }

  const sameLine = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  const isNotSet = !!effective && (effective.type === 'none' || effective.type === 'tbd');
  const savedMatch = effective?.id ? serviceAddresses.find(a => a.id === effective.id) : null;
  let selectedValue = '';
  if (isNotSet) selectedValue = 'none';
  else if (savedMatch) selectedValue = savedMatch.id;
  else if (effective?.type === 'billing' && billing) selectedValue = 'billing';
  else if (effective?.address_line_1 && !effective?.id && billing && sameLine(effective.address_line_1, billing.address_line_1)) selectedValue = 'billing';
  else if (effective?.address_line_1) selectedValue = 'current';
  // A saved "not set yet" stays visible on a record that is no longer a quote, so the picker
  // never pretends an address exists; it is just not offered as a new choice there.
  const showNotSet = allowNotSet || isNotSet;

  const handleSelect = (value) => {
    if (value === 'add') {
      setShowAddForm(true);
      return;
    }
    if (value === 'none') onAddressChange({ ...NOT_SET, customer_id: customer.id });
    else if (value === 'billing' && billing) onAddressChange(billing);
    else if (value === 'current' || value === '') return;
    else {
      const address = serviceAddresses.find(addr => addr.id === value);
      if (address) onAddressChange(address);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="service-address-selector">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {getCustomerTypeIcon()}
          <h4 className="font-medium text-gray-900">Service Address</h4>
          <span className={`text-xs px-2 py-1 rounded-full ${
            customerType === 'commercial' ? 'bg-blue-100 text-blue-800' : customerType === 'industrial' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
          }`}>
            {getCustomerTypeLabel()}
          </span>
        </div>

        <button
          type="button"
          data-testid="service-address-add-toggle"
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
        >
          <PlusIcon className="w-4 h-4" />
          Add address
        </button>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Service Location{allowNotSet ? '' : ' *'}
        </label>
        <div className="relative">
          <select
            data-testid="service-address-select"
            value={selectedValue}
            onChange={(e) => handleSelect(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
          >
            {selectedValue === '' && (
              <option value="">{loading ? 'Loading addresses...' : 'Select a service address...'}</option>
            )}
            {billing && (
              <option value="billing">Customer's address - {formatAddress(billing)}</option>
            )}
            {selectedValue === 'current' && (
              <option value="current">Current: {formatAddress(effective)}</option>
            )}
            {serviceAddresses.length > 0 && (
              <optgroup label="Saved addresses">
                {serviceAddresses.map((address) => (
                  <option key={address.id} value={address.id}>
                    {address.address_name ? `${address.address_name} - ` : ''}{formatAddress(address)}
                    {address.is_primary ? ' (Primary)' : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {showNotSet && <option value="none">Address not set yet</option>}
            <option value="add">+ Add address...</option>
          </select>
          <ChevronDownIcon className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        {isNotSet && (
          <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded-lg" data-testid="service-address-not-set">
            <div className="text-sm text-purple-800">
              <div className="font-medium">Address not set yet</div>
              <div className="mt-1">
                {allowNotSet
                  ? 'Fine for a quote. The address is asked for before the job is scheduled.'
                  : 'This job needs a service address before it can be scheduled.'}
              </div>
            </div>
          </div>
        )}

        {!isNotSet && effective?.address_line_1 && (
          <div className="mt-2 p-3 bg-blue-50 rounded-lg" data-testid="service-address-summary">
            <div className="flex items-center gap-2 mb-1">
              <MapPinIcon className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-blue-900">
                {selectedValue === 'billing' ? "Customer's address" : (effective.address_name || 'Service Location')}
              </span>
              {effective.is_primary && (
                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded-full">Primary</span>
              )}
            </div>
            <div className="text-sm text-blue-800">
              {effective.address_line_1}
              {effective.address_line_2 && <>, {effective.address_line_2}</>}
              <br />
              {[effective.city, effective.state].filter(Boolean).join(', ')} {effective.zip_code || ''}
            </div>
            {effective.access_notes && (
              <div className="text-xs text-blue-700 mt-1">
                <strong>Access:</strong> {effective.access_notes}
              </div>
            )}
          </div>
        )}

        {!loading && !effective && !billing && serviceAddresses.length === 0 && (
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="text-sm text-yellow-800">
              No address on file for this customer. Use Add address to enter one.
            </div>
          </div>
        )}
      </div>

      {/* Add New Address Form */}
      {showAddForm && (
        <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50" data-testid="service-address-add-form">
          <h5 className="font-medium text-gray-900 mb-3">Add service address</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name (optional)
              </label>
              <input
                type="text"
                data-testid="service-address-new-name"
                value={newAddress.address_name}
                onChange={(e) => setNewAddress({ ...newAddress, address_name: e.target.value })}
                placeholder={isResidential ? 'e.g., Rental, Lake house' : 'e.g., Main Office, Warehouse, Building A'}
                className={inputClass}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address *
              </label>
              <input
                type="text"
                data-testid="service-address-new-line1"
                value={newAddress.address_line_1}
                onChange={(e) => setNewAddress({ ...newAddress, address_line_1: e.target.value })}
                className={inputClass}
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit / Apt / Suite
              </label>
              <input
                type="text"
                data-testid="service-address-new-line2"
                value={newAddress.address_line_2}
                onChange={(e) => setNewAddress({ ...newAddress, address_line_2: e.target.value })}
                placeholder="Apt, Suite, Unit, etc."
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <input
                type="text"
                data-testid="service-address-new-city"
                value={newAddress.city}
                onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State *
              </label>
              <StateSelect
                data-testid="service-address-new-state"
                value={newAddress.state}
                onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP Code *
              </label>
              <input
                type="text"
                data-testid="service-address-new-zip"
                value={newAddress.zip_code}
                onChange={(e) => setNewAddress({ ...newAddress, zip_code: e.target.value })}
                className={inputClass}
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Access Instructions
              </label>
              <textarea
                value={newAddress.access_notes}
                onChange={(e) => setNewAddress({ ...newAddress, access_notes: e.target.value })}
                placeholder="Gate codes, parking instructions, contact person, etc."
                rows={2}
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              data-testid="service-address-new-save"
              onClick={handleAddAddress}
              disabled={savingAddress}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {savingAddress ? 'Saving...' : 'Save address'}
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceAddressSelector;
